import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { MarketingAccess, MarketingBanner, MarketingCampaign, AiBoostPackage } from '../lib/marketing/types'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const getToken = () => localStorage.getItem('token')

const parseApiError = async (response: Response, fallback: string) => {
  const text = await response.text()
  try {
    const json = JSON.parse(text)
    return { message: json.error || fallback, code: json.code }
  } catch {
    return { message: text.slice(0, 200) || fallback, code: undefined }
  }
}

async function marketingFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  })
  if (!response.ok) {
    const err = await parseApiError(response, 'Request failed')
    const error = new Error(err.message) as Error & { code?: string }
    error.code = err.code
    throw error
  }
  return response.json()
}

export function useMarketingAccess() {
  return useQuery<MarketingAccess>({
    queryKey: ['marketing', 'access'],
    queryFn: () => marketingFetch('/marketing/access'),
  })
}

export function useMarketingDashboard(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'dashboard'],
    queryFn: () => marketingFetch('/marketing/dashboard'),
    enabled,
  })
}

export function useMarketingAnalytics(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'analytics'],
    queryFn: () => marketingFetch('/marketing/analytics'),
    enabled,
    retry: false,
  })
}

export function useMarketingBanners(enabled = true) {
  return useQuery<MarketingBanner[]>({
    queryKey: ['marketing', 'banners'],
    queryFn: () => marketingFetch('/marketing/banners'),
    enabled,
  })
}

export function useMarketingCampaigns(enabled = true) {
  return useQuery<MarketingCampaign[]>({
    queryKey: ['marketing', 'campaigns'],
    queryFn: () => marketingFetch('/marketing/campaigns'),
    enabled,
  })
}

export function useMarketingHistory(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'history'],
    queryFn: () => marketingFetch('/marketing/history'),
    enabled,
  })
}

export function useContentCalendar(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'calendar'],
    queryFn: () => marketingFetch('/marketing/content-calendar'),
    enabled,
    retry: false,
  })
}

export function useAiBoostPackages() {
  return useQuery<AiBoostPackage[]>({
    queryKey: ['marketing', 'ai-boost-packages'],
    queryFn: () => marketingFetch('/marketing/ai-boost-packages'),
  })
}

export function useAiBoostStatus(enabled = true) {
  return useQuery({
    queryKey: ['marketing', 'ai-boost'],
    queryFn: () => marketingFetch('/marketing/ai-boost'),
    enabled,
  })
}

function invalidateMarketing(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['marketing'] })
}

export function useSaveBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      marketingFetch<MarketingBanner>('/marketing/banners', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Banner berhasil disimpan')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useDeleteBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => marketingFetch(`/marketing/banners/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Banner dihapus')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useDuplicateBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => marketingFetch(`/marketing/banners/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Banner diduplikasi')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useTrackBanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'download' | 'share' }) =>
      marketingFetch(`/marketing/banners/${id}/track`, { method: 'POST', body: JSON.stringify({ action }) }),
    onSuccess: () => invalidateMarketing(queryClient),
  })
}

export function useSaveCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      marketingFetch<MarketingCampaign>('/marketing/campaigns', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Campaign berhasil disimpan')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => marketingFetch(`/marketing/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Campaign dihapus')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => marketingFetch(`/marketing/campaigns/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Campaign diduplikasi')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useTrackCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'download' | 'share' }) =>
      marketingFetch(`/marketing/campaigns/${id}/track`, { method: 'POST', body: JSON.stringify({ action }) }),
    onSuccess: () => invalidateMarketing(queryClient),
  })
}

export function useGenerateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { brandId: number; productId: number; promoType: string }) =>
      marketingFetch('/marketing/ai/generate-campaign', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => invalidateMarketing(queryClient),
    onError: (e: Error & { code?: string }) => {
      if (e.code !== 'AI_BOOST_EXHAUSTED') toast.error(e.message)
    },
  })
}

export function useGenerateCaption() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      marketingFetch('/marketing/ai/generate-caption', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Caption berhasil dibuat')
    },
    onError: (e: Error & { code?: string }) => toast.error(e.message),
  })
}

export function useGenerateAiBannerDesign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      marketingFetch('/marketing/ai/generate-banner-design', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => invalidateMarketing(queryClient),
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useMarketingPlanner() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { brandId: number }) =>
      marketingFetch('/marketing/ai/marketing-planner', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => invalidateMarketing(queryClient),
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useGenerateCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { brandId: number }) =>
      marketingFetch('/marketing/ai/content-calendar', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Kalender konten berhasil dibuat')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useUpdateCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, schedule }: { id: number; schedule: unknown[] }) =>
      marketingFetch(`/marketing/content-calendar/${id}`, { method: 'PUT', body: JSON.stringify({ schedule }) }),
    onSuccess: () => {
      invalidateMarketing(queryClient)
      toast.success('Jadwal diperbarui')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useCreateAiBoostPayment() {
  return useMutation({
    mutationFn: async (packageId: string) => {
      const token = getToken()
      const packages = await marketingFetch<AiBoostPackage[]>('/marketing/ai-boost-packages')
      const selected = packages.find((p) => p.id === packageId)
      if (!selected) throw new Error('Paket tidak ditemukan')

      const response = await fetch(`${API_URL}/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          amount: selected.price,
          paymentType: 'ai_boost',
          packageId: selected.id,
        }),
      })
      if (!response.ok) {
        const err = await parseApiError(response, 'Gagal membuat pembayaran')
        throw new Error(err.message)
      }
      return response.json()
    },
    onSuccess: (data) => {
      if (data.redirectUrl) window.open(data.redirectUrl, '_blank')
    },
    onError: (e: Error) => toast.error(e.message),
  })
}
